import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Newspaper,
  BookOpen,
  Settings,
  Mail,
  User,
  LogOut,
  Menu,
  X,
  Building2,
} from "lucide-react";
import { useAuth } from "./auth";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" },
  { to: "/admin/pages", label: "Pages", icon: FileText, testid: "admin-nav-pages" },
  { to: "/admin/news", label: "News", icon: Newspaper, testid: "admin-nav-news" },
  { to: "/admin/resources", label: "Resources", icon: BookOpen, testid: "admin-nav-resources" },
  { to: "/admin/partners", label: "Partners", icon: Building2, testid: "admin-nav-partners" },
  { to: "/admin/messages", label: "Messages", icon: Mail, testid: "admin-nav-messages" },
  { to: "/admin/settings", label: "Site Settings", icon: Settings, testid: "admin-nav-settings" },
  { to: "/admin/account", label: "Account", icon: User, testid: "admin-nav-account" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
      <aside
        className={[
          "fixed lg:sticky inset-y-0 left-0 z-40 w-72 bg-[hsl(var(--aoa-navy))] text-slate-200 transition-transform duration-200",
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
          "h-screen flex flex-col",
        ].join(" ")}
        data-testid="admin-sidebar"
      >
        <div className="px-6 py-6 border-b border-white/10 flex items-center gap-3">
          <img
            src="/logos/aoa_mark_white.png"
            alt="AOA"
            className="h-10 w-auto object-contain opacity-90"
          />
          <div className="flex flex-col leading-tight">
            <span className="font-display text-sm font-bold text-white">AOA Admin</span>
            <span className="text-[11px] uppercase tracking-[0.16em] text-slate-400">
              Content Studio
            </span>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-1">
          {NAV.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setOpen(false)}
              data-testid={testid}
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm transition-colors",
                  isActive
                    ? "bg-[hsl(var(--aoa-royal))] text-white"
                    : "text-slate-300 hover:bg-white/5 hover:text-white",
                ].join(" ")
              }
            >
              <Icon className="h-4 w-4 flex-none" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/10 px-4 py-4">
          <div className="text-[11px] uppercase tracking-[0.14em] text-slate-400">
            Signed in
          </div>
          <div className="mt-1 text-sm text-white truncate" data-testid="admin-user-email">
            {user?.email}
          </div>
          <button
            onClick={async () => {
              await logout();
              navigate("/admin/login");
            }}
            data-testid="admin-logout"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-200 hover:bg-white hover:text-[hsl(var(--aoa-navy))] transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between px-5 lg:px-10 h-16">
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200"
              data-testid="admin-mobile-toggle"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <div className="font-display text-sm font-semibold text-[hsl(var(--aoa-navy))] hidden md:block">
              AOA Somalia · Content Management
            </div>
            <Link
              to="/"
              target="_blank"
              className="text-xs font-semibold uppercase tracking-[0.12em] text-[hsl(var(--aoa-royal))] hover:underline"
              data-testid="admin-view-public"
            >
              View public site →
            </Link>
          </div>
        </header>
        <main className="flex-1 px-5 lg:px-10 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
